import React, { useState, useMemo } from 'react';
import complianceMappings from '../data/complianceMappings.json';
import './ComplianceDashboard.css';

const FRAMEWORKS = [
  { id: 'owasp2021', name: 'OWASP Top 10 (2021)' },
  { id: 'cisv8', name: 'CIS Controls v8' },
];

function ComplianceDashboard({ findings }) {
  const [framework, setFramework] = useState('owasp2021');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const allFindings = useMemo(() => {
    if (!findings || !Array.isArray(findings)) return [];
    return findings;
  }, [findings]);

  // Map findings to compliance categories
  const mappedCategories = useMemo(() => {
    const frameworkData = complianceMappings[framework];
    if (!frameworkData) return [];

    return Object.entries(frameworkData).map(([id, category]) => {
      const matchedFindings = allFindings.filter(finding => {
        const templateId = (finding.templateId || finding.template || '').toLowerCase();
        return category.templates.some(t => templateId.includes(t.replace(/\/$/, '')));
      });

      return {
        id,
        name: category.name,
        description: category.description,
        templates: category.templates,
        findingCount: matchedFindings.length,
        findings: matchedFindings,
        severity: matchedFindings.length === 0 ? 'none' :
                  matchedFindings.length <= 2 ? 'low' :
                  matchedFindings.length <= 5 ? 'medium' : 'high',
      };
    });
  }, [framework, allFindings]);

  const totalMapped = mappedCategories.reduce((sum, c) => sum + c.findingCount, 0);
  const categoriesWithFindings = mappedCategories.filter(c => c.findingCount > 0).length;

  const selectedCategoryData = selectedCategory
    ? mappedCategories.find(c => c.id === selectedCategory)
    : null;

  return (
    <div className="compliance-container">
      {/* Framework Selector */}
      <div className="compliance-header">
        <div className="compliance-framework-selector">
          <label className="compliance-label">Framework</label>
          <select
            className="compliance-select"
            value={framework}
            onChange={(e) => { setFramework(e.target.value); setSelectedCategory(null); }}
          >
            {FRAMEWORKS.map(fw => (
              <option key={fw.id} value={fw.id}>{fw.name}</option>
            ))}
          </select>
        </div>
        <div className="compliance-stats">
          <div className="compliance-stat">
            <span className="compliance-stat-value">{totalMapped}</span>
            <span className="compliance-stat-label">Mapped Findings</span>
          </div>
          <div className="compliance-stat">
            <span className="compliance-stat-value">{categoriesWithFindings}/{mappedCategories.length}</span>
            <span className="compliance-stat-label">Categories Affected</span>
          </div>
        </div>
      </div>

      {/* Heat Map */}
      <div className="compliance-heatmap">
        {mappedCategories.map((cat) => (
          <button
            key={cat.id}
            className={`compliance-cell ${selectedCategory === cat.id ? 'selected' : ''}`}
            data-severity={cat.severity}
            onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
            title={`${cat.name}: ${cat.findingCount} finding(s)`}
          >
            <span className="compliance-cell-id">{cat.id}</span>
            <span className="compliance-cell-name">{cat.name}</span>
            <span className="compliance-cell-count" data-severity={cat.severity}>
              {cat.findingCount}
            </span>
          </button>
        ))}
      </div>

      {/* Selected Category Detail */}
      {selectedCategoryData && (
        <div className="compliance-detail">
          <div className="compliance-detail-header">
            <h3 className="compliance-detail-title">
              <span className="compliance-detail-id">{selectedCategoryData.id}</span>
              {selectedCategoryData.name}
            </h3>
            <p className="compliance-detail-desc">{selectedCategoryData.description}</p>
          </div>

          {selectedCategoryData.findings.length > 0 ? (
            <div className="compliance-findings-list">
              {selectedCategoryData.findings.map((finding, i) => (
                <div key={i} className="compliance-finding-row" data-severity={finding.severity?.toLowerCase()}>
                  <span className="compliance-finding-severity" data-severity={finding.severity?.toLowerCase()}>
                    {(finding.severity || 'INFO').toUpperCase()}
                  </span>
                  <span className="compliance-finding-name">{finding.name || finding.templateId}</span>
                  {finding.matched && (
                    <span className="compliance-finding-matched">{finding.matched}</span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="compliance-no-findings">
              No findings mapped to this category. Your configuration appears compliant.
            </div>
          )}

          <div className="compliance-templates">
            <span className="compliance-templates-label">Monitored templates:</span>
            {selectedCategoryData.templates.map((t, i) => (
              <span key={i} className="compliance-template-tag">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="compliance-legend">
        <span className="compliance-legend-item" data-severity="none">0 findings</span>
        <span className="compliance-legend-item" data-severity="low">1-2 findings</span>
        <span className="compliance-legend-item" data-severity="medium">3-5 findings</span>
        <span className="compliance-legend-item" data-severity="high">6+ findings</span>
      </div>
    </div>
  );
}

export default ComplianceDashboard;
